// Light-DOM stylesheet self-injection for @raventic/sdk.
//
// A customer bundle may ship a `<head>` stylesheet next to it (`widget.css`: Vite-emitted
// component CSS + the customer's compiled `styles/light.scss`, see scripts/lib/styles.ts).
// There is no loader script any more — the customer embeds ONLY
// `<script src=".../widget.js" async>` — so the bundle links its own stylesheet at boot.
//
// The URL is a build-time constant: build-customer.ts bakes `process.env.RAVENTIC_LIGHT_CSS_URL`
// (scripts/lib/bundle.ts `buildDefineMap` + `bakeLightCssUrl`) to the absolute
// `<cdnBase>/<uuid>/widget.css` when the build produced one, or to "" when it did not
// (no <link> → no 404 on every page load). The dev harness bakes "" and links the
// stylesheet from its own page instead.
//
// Deliberately import-free (no SDK runtime), so it unit-tests under plain Node with a
// stub document (packages/sdk/test/light-styles.test.ts).

// Append `<link rel="stylesheet" href>` to <head> unless `href` is empty/undefined or a
// stylesheet link with that href is already present (a host that also hand-links
// widget.css, or a second copy of the bundle on the page, must not double-load it).
// Returns whether a link was appended.
//
// "Stylesheet link" = `rel` CONTAINS the token, case-insensitively (`~=` + the `i` flag),
// like the browser's own rel matching — so a hand-written `rel="Stylesheet"` or
// `rel="stylesheet alternate"` still counts, while a `rel="preload"` of the same URL
// (which doesn't apply the CSS) does not.
export function injectLightStylesheet(href: string | undefined, doc: Document = document): boolean {
  if (!href) return false;
  const links = doc.querySelectorAll<HTMLLinkElement>('link[rel~="stylesheet" i]');
  for (const link of Array.from(links)) {
    // Compare both the literal attribute and the resolved `.href`, so a host link written
    // as a relative/protocol-relative URL to the same file still counts as present.
    if (link.getAttribute("href") === href || link.href === href) return false;
  }
  const link = doc.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  (doc.head ?? doc.getElementsByTagName("head")[0]).appendChild(link);
  return true;
}
